import { useEffect, useState } from 'react'
import api, { IOrdinalProvenanceResponse } from 'api/api'
import { subscribeOrdinalState } from 'state/ordinalRealtime'
import { ordinalProvenanceRetryDelayMs } from './ordinalProvenanceRefreshPolicy'

// Session-scoped: fresh snapshots are shared by the artifact header and menu
// so opening the menu costs nothing. Pending/stale/error snapshots are polled
// until a background refresh makes them fresh.
const provenanceByOrigin = new Map<string, IOrdinalProvenanceResponse>()
const provenanceRequestByOrigin = new Map<string, Promise<IOrdinalProvenanceResponse | null>>()
const provenanceRefreshByOrigin = new Map<string, Promise<IOrdinalProvenanceResponse | null>>()
const invalidatedRevisionByOrigin = new Map<string, number>()
const mountedOrigins = new Map<string, { ordinalId?: string; loads: Set<() => void> }>()

// Only mounted headers take part in reconnect recovery. Unresolved headers
// have no ID for the batch endpoint yet, so share a fresh provenance request.
export function recoverMountedOrdinalProvenance(): string[] {
    // Unmounted headers can reopen after the network gap too. Discard their
    // snapshots without fetching them; mounted IDs still join batch recovery.
    provenanceByOrigin.clear()
    provenanceRequestByOrigin.clear()
    provenanceRefreshByOrigin.clear()
    const ids: string[] = []
    mountedOrigins.forEach((entry, origin) => {
        if (entry.ordinalId) ids.push(entry.ordinalId)
        else {
            forgetOrdinalProvenance(origin)
            entry.loads.forEach(load => load())
        }
    })
    return ids
}

export function loadOrdinalProvenance(origin: string): Promise<IOrdinalProvenanceResponse | null> {
    const cached = provenanceByOrigin.get(origin)
    if (cached) return Promise.resolve(cached)

    const pending = provenanceRequestByOrigin.get(origin)
    if (pending) return pending

    const request = api
        .getOrdinalProvenance(origin)
        .then(res => {
            // A sale may invalidate this request while it is in flight. Its
            // response must update neither the cache nor a mounted header.
            if (provenanceRequestByOrigin.get(origin) !== request) return null
            if (!res?.ok) return null
            if (!res.chain || res.chain.status === 'fresh') {
                provenanceByOrigin.set(origin, res)
            }
            return res
        })
        .catch(() => null)

    provenanceRequestByOrigin.set(origin, request)
    void request.then(() => {
        if (provenanceRequestByOrigin.get(origin) === request) {
            provenanceRequestByOrigin.delete(origin)
        }
    })

    return request
}

// Synchronous cache inspection lets click paths distinguish a stale session
// snapshot from the fresh network response that loadOrdinalProvenance just
// obtained. Callers must still use load/refresh when this returns null.
export function peekOrdinalProvenance(origin: string): IOrdinalProvenanceResponse | null {
    return provenanceByOrigin.get(origin) || null
}

// A navigation decision needs the current thread pointer: another reader may
// have materialized the canonical thread since this session cached a nil one.
// Bypass the snapshot for that decision, while coalescing concurrent refreshes.
export function refreshOrdinalProvenance(
    origin: string,
): Promise<IOrdinalProvenanceResponse | null> {
    const refreshing = provenanceRefreshByOrigin.get(origin)
    if (refreshing) return refreshing

    provenanceByOrigin.delete(origin)
    provenanceRequestByOrigin.delete(origin)
    const request = loadOrdinalProvenance(origin).finally(() => {
        if (provenanceRefreshByOrigin.get(origin) === request) {
            provenanceRefreshByOrigin.delete(origin)
        }
    })
    provenanceRefreshByOrigin.set(origin, request)
    return request
}

// Owner-only state (starring and archiving) rides the same payload, so a local toggle has to
// invalidate the entry rather than let the cache serve a stale flag.
export function forgetOrdinalProvenance(
    origin: string | null | undefined,
    revision?: number,
): void {
    if (!origin) return
    // The shared listener and several headers can receive the same revision
    // on multiple socket lanes. They must share one post-event request.
    if (revision !== undefined) {
        if (revision <= (invalidatedRevisionByOrigin.get(origin) ?? -1)) return
        invalidatedRevisionByOrigin.set(origin, revision)
    }
    provenanceByOrigin.delete(origin)
    provenanceRequestByOrigin.delete(origin)
    provenanceRefreshByOrigin.delete(origin)
}

export function useOrdinalProvenance(origin: string | null): IOrdinalProvenanceResponse | null {
    const [loaded, setLoaded] = useState<{
        origin: string
        value: IOrdinalProvenanceResponse
    } | null>(null)

    useEffect(() => {
        if (!origin) return
        let cancelled = false
        let retryTimer: number | null = null
        const mounted = mountedOrigins.get(origin) || {
            ordinalId: provenanceByOrigin.get(origin)?.bsv_ordinal_id,
            loads: new Set<() => void>(),
        }
        mountedOrigins.set(origin, mounted)

        const load = () => {
            if (retryTimer !== null) window.clearTimeout(retryTimer)
            retryTimer = null
            loadOrdinalProvenance(origin).then(res => {
                if (cancelled) return
                if (res) {
                    mounted.ordinalId = res.bsv_ordinal_id
                    setLoaded({ origin, value: res })
                    if (retryTimer !== null) window.clearTimeout(retryTimer)
                    retryTimer = null
                }

                const retryDelay = ordinalProvenanceRetryDelayMs(res)
                if (retryDelay !== null) {
                    retryTimer = window.setTimeout(() => {
                        forgetOrdinalProvenance(origin)
                        load()
                    }, retryDelay)
                }
            })
        }

        mounted.loads.add(load)
        load()

        // Ownership and chain state both live in this payload, so a sale or a
        // block confirmation has to reach a header that is already mounted —
        // otherwise it keeps its snapshot until its retry timer happens to fire.
        const unsubscribe = subscribeOrdinalState(event => {
            if (event.origin !== origin) return
            forgetOrdinalProvenance(origin, event.revision)
            load()
        })

        return () => {
            cancelled = true
            mounted.loads.delete(load)
            if (!mounted.loads.size) mountedOrigins.delete(origin)
            unsubscribe()
            if (retryTimer !== null) window.clearTimeout(retryTimer)
        }
    }, [origin])

    if (!origin) return null
    return loaded?.origin === origin ? loaded.value : provenanceByOrigin.get(origin) || null
}
